import { getSearchHistory, addToSearchHistory } from './../helpers'
import type { GlobalSearchResultPreview } from '../global-search-models'
import type { PopoverComponent, SearchFieldComponent } from '@getgo/chameleon-web'
import { html, property, query } from 'lit-element'
import { ShellElement } from '../../../common/shell-element'
import { asTranslationKey } from '../../../common/translate-helpers/i18n-utils'
import { getGlobalSearchManager } from '../global-search-manager'
import { SEARCH_ROUTE } from '../../../common/routes'
import { navigateToUrl } from '../../../common'
import globalSearch from './global-search-field.styles.scss'

export interface GoToGlobalSearchFieldProps {
  readonly searchResults: readonly GlobalSearchResultPreview[]
}
export class GoToGlobalSearchField extends ShellElement implements GoToGlobalSearchFieldProps {
  static readonly tagName = 'goto-global-search'

  @property({ type: Boolean }) showResults = true
  @property({ type: String }) searchString = ''
  @property({ type: Array, attribute: 'searchResults' })
  public searchResults: GoToGlobalSearchFieldProps['searchResults'] = []
  @property({ type: Array }) searchHistory: string[] = []

  @query('chameleon-search-field') private searchField: SearchFieldComponent | undefined
  @query('chameleon-popover') private chameleonPopover: PopoverComponent | undefined

  private placeholder = asTranslationKey('Search contacts')

  private resizeObserver = new ResizeObserver(elements => {
    elements.forEach(element => {
      this.setSearchResultsWidth(element.target)
    })
  })

  static get styles() {
    return globalSearch
  }

  firstUpdated() {
    if (this.searchField) {
      this.resizeObserver.observe(this.searchField)
      this.unsubscribeFunctions.push(() => this.resizeObserver.unobserve(this.searchField as Element))
      this.setSearchResultsWidth(this.searchField)
    }
  }

  render() {
    const categories = getGlobalSearchManager().getCategories()
    if (!categories?.length) {
      return html``
    }
    return html`
      <chameleon-popover hidden>
        <chameleon-search-field
          slot="trigger"
          placeholder=${this.placeholder}
          @keyup=${(event: KeyboardEvent) => this.triggerSearch(event)}
          @focus=${() => this.setSearchHistory()}
        >
        </chameleon-search-field>

        <div slot="content">${this.renderPopoverContent()}</div>
      </chameleon-popover>
    `
  }

  private renderPopoverContent() {
    return html` ${this.searchString
      ? html`<goto-search-result
          @viewmoreresultsclick=${this.viewMoreResultsButtonClickHandler}
          searchString=${this.searchString}
          .searchResults=${this.searchResults}
        ></goto-search-result> `
      : html` <goto-search-history .searchHistory=${this.searchHistory}></goto-search-history>`}`
  }

  private async setSearchHistory() {
    this.searchHistory = await getSearchHistory()
  }

  private setSearchResultsWidth(element: Element) {
    this.chameleonPopover?.setAttribute('width', Math.max(400, element.clientWidth).toString() ?? 0)
  }

  // The following function is a temporary hack while chameleon team fixes https://jira.ops.expertcity.com/browse/CHAMELEON-1689
  private triggerSearch(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      //addToSearchHistory on enter will remain even when the rest of this function is removed
      this.searchString = this.searchField?.value ?? ''
      if (this.searchString.trim()) {
        addToSearchHistory(this.searchString)
        getGlobalSearchManager()
          .searchFor(this.searchString)
          .then(results => (this.searchResults = getGlobalSearchManager().convertToGlobalSearchResultPreviews(results)))
      }
    }
  }

  private closeChameleonPopover() {
    this.chameleonPopover?.close()
  }

  private viewMoreResultsButtonClickHandler = () => {
    // We'll have to do the same actions below on enter when the chameleon popover functionality is fixed
    addToSearchHistory(this.searchString)
    this.closeChameleonPopover()
    const params = new URLSearchParams()
    params.append('query', this.searchString)
    navigateToUrl(`${SEARCH_ROUTE}?${params.toString()}`)
  }
}

declare global {
  interface HTMLElementTagNameMap {
    readonly [GoToGlobalSearchField.tagName]: GoToGlobalSearchField
  }
}
